// src/components/admin/AdminContentMap.tsx
import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Folder, BookOpen, Tag, FileText, ChevronRight, ChevronDown,
    Search, X, Globe, EyeOff, Copy, Check, ExternalLink, RefreshCw, Map
} from 'lucide-react';
import './AdminContentMap.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SubdeckNode {
    subdeckId: string;
    subdeckName: string;
    cardCount: number;
    apostilaCount: number;
    type: string;
    isBlocked?: boolean;
    isFree?: boolean;
}

interface GroupNode {
    groupId: string;
    groupName: string;
    groupIcon: string;
    groupColor: string;
    totalCards: number;
    subdecks: SubdeckNode[];
}

interface DeckNode {
    deckId: string;
    deckName: string;
    deckDescription: string;
    iconUrl: string | null;
    totalSubdecks: number;
    totalCards: number;
    publicVisible: boolean;
    publicSlug: string | null;
    previewCardsCount: number;
    groups: GroupNode[];
    ungroupedSubdecks: SubdeckNode[];
    createdAt?: string;
}

interface FieldNode {
    fieldId: string;
    fieldName: string;
    cycleColor?: string;
    cycleIcon?: string;
    totalDecks: number;
    totalSubdecks: number;
    totalCards: number;
    decks: DeckNode[];
}

type SelectedNode =
    | { type: 'field'; data: FieldNode }
    | { type: 'deck'; data: DeckNode; fieldName: string }
    | { type: 'group'; data: GroupNode; deckName: string; fieldName: string }
    | { type: 'subdeck'; data: SubdeckNode; groupName?: string; deckName: string; fieldName: string };

// ─── API helpers ──────────────────────────────────────────────────────────────

const API = '/api';

async function fetchContentMap(): Promise<FieldNode[]> {
    const token = localStorage.getItem('accessToken');
    const res = await fetch(`${API}/admin/content-map`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Erro ao carregar mapa de conteúdo');
    return res.json();
}

async function updateDeckVisibility(deckId: string, payload: {
    publicVisible?: boolean;
    publicSlug?: string;
    previewCardsCount?: number;
}): Promise<{ success: boolean; deck: { publicVisible: boolean; publicSlug: string | null; previewCardsCount: number } }> {
    const token = localStorage.getItem('accessToken');
    const res = await fetch(`${API}/admin/content-map/deck/${deckId}/visibility`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
    });
    let body: any;
    try { body = await res.json(); } catch { body = {}; }
    if (!res.ok) throw new Error(body?.message || `Erro ao salvar (${res.status})`);
    return body;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function Badge({ children, color }: { children: React.ReactNode; color?: string }) {
    return (
        <span className="acm-badge" style={color ? { background: color } : undefined}>
            {children}
        </span>
    );
}

function Counter({ value, label }: { value: number; label: string }) {
    return (
        <span className="acm-counter">
            <strong>{value.toLocaleString('pt-BR')}</strong> {label}
        </span>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface AdminContentMapProps {
    onShowToast?: (msg: string, type: 'success' | 'error') => void;
}

const AdminContentMap: React.FC<AdminContentMapProps> = ({ onShowToast }) => {
    const queryClient = useQueryClient();

    // Estado de expansão persistido no localStorage
    const [openNodes, setOpenNodes] = useState<Set<string>>(() => {
        try {
            const saved = localStorage.getItem('graia_content_map_open');
            return saved ? new Set(JSON.parse(saved)) : new Set<string>();
        } catch {
            return new Set<string>();
        }
    });

    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<SelectedNode | null>(null);
    const [copied, setCopied] = useState(false);
    const [copiedOrg, setCopiedOrg] = useState(false);
    const [editingSlug, setEditingSlug] = useState<string>('');
    const [slugEditing, setSlugEditing] = useState(false);

    const { data: fields = [], isLoading, error, dataUpdatedAt } = useQuery({
        queryKey: ['admin-content-map'],
        queryFn: fetchContentMap,
        staleTime: 30_000,
        refetchInterval: 60_000,
    });

    const visibilityMutation = useMutation({
        mutationFn: ({ deckId, payload }: { deckId: string; payload: Parameters<typeof updateDeckVisibility>[1] }) =>
            updateDeckVisibility(deckId, payload),
        onSuccess: (result, variables) => {
            // Atualiza o selected localmente para evitar estado stale
            if (result?.deck) {
                setSelected(prev => {
                    if (!prev || prev.type !== 'deck') return prev;
                    return {
                        ...prev,
                        data: {
                            ...prev.data,
                            publicVisible: result.deck.publicVisible,
                            publicSlug: result.deck.publicSlug,
                            previewCardsCount: result.deck.previewCardsCount,
                        },
                    };
                });
            }
            queryClient.invalidateQueries({ queryKey: ['admin-content-map'] });
            onShowToast?.('Salvo com sucesso', 'success');
        },
        onError: (err: Error) => {
            onShowToast?.(err.message, 'error');
        },
    });

    // Persistir estado de abertura
    const persistOpen = useCallback((next: Set<string>) => {
        localStorage.setItem('graia_content_map_open', JSON.stringify([...next]));
    }, []);

    const toggle = useCallback((id: string) => {
        setOpenNodes(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            persistOpen(next);
            return next;
        });
    }, [persistOpen]);

    // ── Busca: calcula quais nós fazem match e quais ancestrais abrir ──
    const searchLower = search.trim().toLowerCase();

    const { filteredFields, autoOpen } = useMemo(() => {
        if (!searchLower) return { filteredFields: fields, autoOpen: new Set<string>() };

        const autoOpen = new Set<string>();

        const filtered = fields.map((field: FieldNode) => {
            const fieldMatch = field.fieldName.toLowerCase().includes(searchLower);

            const filteredDecks = field.decks.map((deck: DeckNode) => {
                const deckMatch = deck.deckName.toLowerCase().includes(searchLower);

                const filteredGroups = deck.groups.map((group: GroupNode) => {
                    const groupMatch = group.groupName.toLowerCase().includes(searchLower);
                    const filteredSubs = group.subdecks.filter((sd: SubdeckNode) =>
                        sd.subdeckName.toLowerCase().includes(searchLower)
                    );
                    if (filteredSubs.length > 0 || groupMatch) {
                        autoOpen.add(field.fieldId);
                        autoOpen.add(deck.deckId);
                        autoOpen.add(group.groupId);
                        return { ...group, subdecks: groupMatch ? group.subdecks : filteredSubs };
                    }
                    return null;
                }).filter(Boolean) as GroupNode[];

                const filteredUngrouped = deck.ungroupedSubdecks.filter((sd: SubdeckNode) =>
                    sd.subdeckName.toLowerCase().includes(searchLower)
                );

                if (filteredUngrouped.length > 0) {
                    autoOpen.add(field.fieldId);
                    autoOpen.add(deck.deckId);
                }

                if (deckMatch || filteredGroups.length > 0 || filteredUngrouped.length > 0) {
                    if (!deckMatch) autoOpen.add(field.fieldId);
                    return {
                        ...deck,
                        groups: deckMatch ? deck.groups : filteredGroups,
                        ungroupedSubdecks: deckMatch ? deck.ungroupedSubdecks : filteredUngrouped,
                    };
                }
                return null;
            }).filter(Boolean) as DeckNode[];

            if (fieldMatch || filteredDecks.length > 0) {
                return { ...field, decks: fieldMatch ? field.decks : filteredDecks };
            }
            return null;
        }).filter(Boolean) as FieldNode[];

        return { filteredFields: filtered, autoOpen };
    }, [fields, searchLower]);

    const effectiveOpen = useMemo(() => {
        if (!searchLower) return openNodes;
        return new Set([...openNodes, ...autoOpen]);
    }, [openNodes, autoOpen, searchLower]);

    // ── Totais globais ──
    const totals = useMemo(() => {
        let decks = 0, subdecks = 0, cards = 0;
        for (const f of fields) {
            decks += f.totalDecks;
            subdecks += f.totalSubdecks;
            cards += f.totalCards;
        }
        return { fields: fields.length, decks, subdecks, cards };
    }, [fields]);

    // ── Copiar organização como texto ──
    const copyOrgTree = useCallback(() => {
        const lines: string[] = [];
        for (const field of fields) {
            lines.push(`📁 ${field.fieldName} · ${field.totalDecks} decks · ${field.totalCards.toLocaleString('pt-BR')} cards`);
            for (const deck of field.decks) {
                lines.push(`  📖 ${deck.deckName} · ${deck.totalSubdecks} submódulos · ${deck.totalCards.toLocaleString('pt-BR')} cards`);
                for (const group of deck.groups) {
                    lines.push(`    🏷️ ${group.groupName} · ${group.subdecks.length} subdecks · ${group.totalCards.toLocaleString('pt-BR')} cards`);
                    for (const sd of group.subdecks) {
                        if (sd.type === 'apostila') {
                            lines.push(`       📄 ${sd.subdeckName} · Apostila`);
                        } else {
                            lines.push(`       📄 ${sd.subdeckName} · ${sd.cardCount.toLocaleString('pt-BR')} cards`);
                        }
                    }
                }
                for (const sd of deck.ungroupedSubdecks) {
                    if (sd.type === 'apostila') {
                        lines.push(`    📄 ${sd.subdeckName} · Apostila`);
                    } else {
                        lines.push(`    📄 ${sd.subdeckName} · ${sd.cardCount.toLocaleString('pt-BR')} cards`);
                    }
                }
            }
        }
        navigator.clipboard.writeText(lines.join('\n')).then(() => {
            setCopiedOrg(true);
            setTimeout(() => setCopiedOrg(false), 2000);
        });
    }, [fields]);

    // ── Copiar link ──
    const copyLink = useCallback((slug: string) => {
        const url = `${window.location.origin}/preview/${slug}`;
        navigator.clipboard.writeText(url).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    }, []);

    // ── Painel lateral: deck selecionado ──
    const selectedDeck = selected?.type === 'deck' ? selected.data : null;

    const handleToggleVisibility = () => {
        if (!selectedDeck) return;
        const newVal = !selectedDeck.publicVisible;
        const slug = selectedDeck.publicSlug || selectedDeck.deckName
            .toLowerCase()
            .normalize('NFD')
            .replace(/[̀-ͯ]/g, '')
            .replace(/[^a-z0-9-]/g, '-')
            .replace(/-+/g, '-');

        visibilityMutation.mutate({
            deckId: selectedDeck.deckId,
            payload: { publicVisible: newVal, ...(newVal && !selectedDeck.publicSlug ? { publicSlug: slug } : {}) },
        });
    };

    const handleSaveSlug = () => {
        if (!selectedDeck || !editingSlug.trim()) return;
        visibilityMutation.mutate({
            deckId: selectedDeck.deckId,
            payload: { publicSlug: editingSlug.trim() },
        });
        setSlugEditing(false);
    };

    // ─── Render helpers ───────────────────────────────────────────────────────

    const renderSubdeck = (sd: SubdeckNode, context: { deckName: string; fieldName: string; groupName?: string }) => {
        const isApostila = sd.type === 'apostila';
        return (
            <div
                key={sd.subdeckId}
                className={`acm-subdeck ${isApostila ? 'apostila' : ''} ${selected?.type === 'subdeck' && selected.data.subdeckId === sd.subdeckId ? 'selected' : ''}`}
                onClick={() => setSelected({ type: 'subdeck', data: sd, ...context })}
            >
                <FileText size={14} className={isApostila ? 'acm-icon-apostila' : 'acm-icon-subdeck'} />
                <span className="acm-node-name">{sd.subdeckName}</span>
                <div className="acm-node-counters">
                    {isApostila
                        ? <Badge color="#7c3aed">Apostila</Badge>
                        : <Counter value={sd.cardCount} label="cards" />
                    }
                    {!isApostila && sd.apostilaCount > 0 && <Badge color="#003BD1">+Apostila</Badge>}
                    {sd.isFree && <Badge color="#0a6f3c">Free</Badge>}
                </div>
            </div>
        );
    };

    const renderGroup = (group: GroupNode, deck: DeckNode, fieldName: string) => {
        const isOpen = effectiveOpen.has(group.groupId);
        return (
            <div key={group.groupId} className="acm-group-wrap">
                <div
                    className={`acm-group ${selected?.type === 'group' && selected.data.groupId === group.groupId ? 'selected' : ''}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        setSelected({ type: 'group', data: group, deckName: deck.deckName, fieldName });
                        toggle(group.groupId);
                    }}
                >
                    {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <Tag size={14} className="acm-icon-group" style={{ color: group.groupColor }} />
                    <span className="acm-node-name">{group.groupName}</span>
                    <div className="acm-node-counters">
                        <Counter value={group.totalCards} label="cards" />
                        <Counter value={group.subdecks.length} label="subdecks" />
                    </div>
                </div>
                {isOpen && (
                    <div className="acm-children">
                        {group.subdecks.map(sd => renderSubdeck(sd, { deckName: deck.deckName, fieldName, groupName: group.groupName }))}
                    </div>
                )}
            </div>
        );
    };

    const renderDeck = (deck: DeckNode, fieldName: string) => {
        const isOpen = effectiveOpen.has(deck.deckId);
        return (
            <div key={deck.deckId} className="acm-deck-wrap">
                <div
                    className={`acm-deck ${selected?.type === 'deck' && selected.data.deckId === deck.deckId ? 'selected' : ''}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        setSelected({ type: 'deck', data: deck, fieldName });
                        toggle(deck.deckId);
                    }}
                >
                    {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    {deck.iconUrl
                        ? <img src={deck.iconUrl} alt="" className="acm-deck-icon" />
                        : <BookOpen size={16} className="acm-icon-deck" />
                    }
                    <span className="acm-node-name">{deck.deckName}</span>
                    <div className="acm-node-counters">
                        <Counter value={deck.totalSubdecks} label="sub" />
                        <Counter value={deck.totalCards} label="cards" />
                        {deck.publicVisible && (
                            <span className="acm-public-pill">
                                <Globe size={11} /> público
                            </span>
                        )}
                    </div>
                </div>
                {isOpen && (
                    <div className="acm-children">
                        {deck.groups.map(g => renderGroup(g, deck, fieldName))}
                        {deck.ungroupedSubdecks.map(sd => renderSubdeck(sd, { deckName: deck.deckName, fieldName }))}
                    </div>
                )}
            </div>
        );
    };

    const renderField = (field: FieldNode) => {
        const isOpen = effectiveOpen.has(field.fieldId);
        const color = field.cycleColor || '#003BD1';
        return (
            <div key={field.fieldId} className="acm-field-wrap">
                <div
                    className={`acm-field ${selected?.type === 'field' && selected.data.fieldId === field.fieldId ? 'selected' : ''}`}
                    style={{ '--field-color': color } as React.CSSProperties}
                    onClick={() => {
                        setSelected({ type: 'field', data: field });
                        toggle(field.fieldId);
                    }}
                >
                    {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    <Folder size={18} style={{ color }} />
                    <span className="acm-node-name">{field.fieldName}</span>
                    <div className="acm-node-counters">
                        <Counter value={field.totalDecks} label="decks" />
                        <Counter value={field.totalCards} label="cards" />
                    </div>
                </div>
                {isOpen && (
                    <div className="acm-children" style={{ borderLeftColor: `${color}40` }}>
                        {field.decks.map(d => renderDeck(d, field.fieldName))}
                    </div>
                )}
            </div>
        );
    };

    // ─── Painel lateral de detalhes ────────────────────────────────────────────

    const renderDetailPanel = () => {
        if (!selected) {
            return (
                <div className="acm-detail-empty">
                    <div className="acm-detail-empty-icon">
                        <Map size={28} />
                    </div>
                    <p className="acm-detail-empty-title">Nenhum item selecionado</p>
                    <p className="acm-detail-empty-hint">Clique em um campo, deck, grupo ou subdeck para ver os detalhes aqui</p>
                </div>
            );
        }

        if (selected.type === 'field') {
            const f = selected.data;
            return (
                <div className="acm-detail-content">
                    <div className="acm-detail-header">
                        <Folder size={22} className="acm-icon-field" />
                        <h3>{f.fieldName}</h3>
                    </div>
                    <div className="acm-detail-stats">
                        <div className="acm-stat-pill"><span>{f.totalDecks}</span> Decks</div>
                        <div className="acm-stat-pill"><span>{f.totalSubdecks}</span> Subdecks</div>
                        <div className="acm-stat-pill"><span>{f.totalCards.toLocaleString('pt-BR')}</span> Cards</div>
                    </div>
                    <div className="acm-detail-section">
                        <h4>Decks neste campo</h4>
                        {f.decks.map(d => (
                            <div key={d.deckId} className="acm-detail-list-item"
                                onClick={() => setSelected({ type: 'deck', data: d, fieldName: f.fieldName })}>
                                <BookOpen size={14} />
                                <span>{d.deckName}</span>
                                <span className="acm-detail-list-count">{d.totalCards} cards</span>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        if (selected.type === 'deck') {
            const d = selected.data;
            const slug = d.publicSlug || '';
            return (
                <div className="acm-detail-content">
                    <div className="acm-detail-header">
                        {d.iconUrl
                            ? <img src={d.iconUrl} alt="" className="acm-deck-icon-lg" />
                            : <BookOpen size={22} className="acm-icon-deck" />}
                        <h3>{d.deckName}</h3>
                    </div>
                    {d.deckDescription && <p className="acm-detail-desc">{d.deckDescription}</p>}
                    <div className="acm-detail-stats">
                        <div className="acm-stat-pill"><span>{d.totalSubdecks}</span> Subdecks</div>
                        <div className="acm-stat-pill"><span>{d.totalCards.toLocaleString('pt-BR')}</span> Cards</div>
                        <div className="acm-stat-pill"><span>{d.groups.length}</span> Grupos</div>
                    </div>

                    <div className="acm-detail-section">
                        <h4>Visibilidade Pública</h4>
                        <div className="acm-visibility-row">
                            <button
                                className={`acm-toggle ${d.publicVisible ? 'on' : 'off'}`}
                                onClick={handleToggleVisibility}
                                disabled={visibilityMutation.isPending}
                            >
                                {d.publicVisible ? <Globe size={14} /> : <EyeOff size={14} />}
                                {d.publicVisible ? 'Público' : 'Privado'}
                            </button>
                        </div>

                        {d.publicVisible && (
                            <div className="acm-slug-section">
                                <label className="acm-label">Slug da URL pública</label>
                                {slugEditing ? (
                                    <div className="acm-slug-edit-row">
                                        <input
                                            className="acm-slug-input"
                                            value={editingSlug}
                                            onChange={e => setEditingSlug(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') handleSaveSlug(); if (e.key === 'Escape') setSlugEditing(false); }}
                                            autoFocus
                                        />
                                        <button className="acm-btn-sm primary" onClick={handleSaveSlug}>Salvar</button>
                                        <button className="acm-btn-sm ghost" onClick={() => setSlugEditing(false)}>Cancelar</button>
                                    </div>
                                ) : (
                                    <div className="acm-slug-display-row">
                                        <code className="acm-slug-code">/preview/{slug || '...'}</code>
                                        <button className="acm-icon-btn" title="Editar slug"
                                            onClick={() => { setEditingSlug(slug); setSlugEditing(true); }}>
                                            ✏️
                                        </button>
                                        <button className="acm-icon-btn" title="Copiar link"
                                            onClick={() => slug && copyLink(slug)}>
                                            {copied ? <Check size={14} /> : <Copy size={14} />}
                                        </button>
                                        {slug && (
                                            <a className="acm-icon-btn" href={`/preview/${slug}`} target="_blank" rel="noopener noreferrer" title="Abrir vitrine">
                                                <ExternalLink size={14} />
                                            </a>
                                        )}
                                    </div>
                                )}

                                <label className="acm-label" style={{ marginTop: 8 }}>Preview de cards por subdeck</label>
                                <input
                                    type="number"
                                    min={1}
                                    max={10}
                                    className="acm-slug-input"
                                    style={{ width: 80 }}
                                    defaultValue={d.previewCardsCount}
                                    onBlur={e => {
                                        const v = parseInt(e.target.value);
                                        if (!isNaN(v) && v !== d.previewCardsCount) {
                                            visibilityMutation.mutate({ deckId: d.deckId, payload: { previewCardsCount: v } });
                                        }
                                    }}
                                />
                            </div>
                        )}
                    </div>

                    <div className="acm-detail-section">
                        <h4>Grupos</h4>
                        {d.groups.length === 0 && <p className="acm-detail-empty-text">Sem grupos</p>}
                        {d.groups.map(g => (
                            <div key={g.groupId} className="acm-detail-list-item"
                                onClick={() => setSelected({ type: 'group', data: g, deckName: d.deckName, fieldName: selected.fieldName })}>
                                <Tag size={14} style={{ color: g.groupColor }} />
                                <span>{g.groupName}</span>
                                <span className="acm-detail-list-count">{g.totalCards} cards</span>
                            </div>
                        ))}
                        {d.ungroupedSubdecks.length > 0 && (
                            <div className="acm-detail-list-item muted">
                                <FileText size={14} />
                                <span>{d.ungroupedSubdecks.length} subdeck(s) sem grupo</span>
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        if (selected.type === 'group') {
            const g = selected.data;
            return (
                <div className="acm-detail-content">
                    <div className="acm-detail-header">
                        <Tag size={22} style={{ color: g.groupColor }} />
                        <h3>{g.groupName}</h3>
                    </div>
                    <p className="acm-detail-breadcrumb">{selected.fieldName} › {selected.deckName}</p>
                    <div className="acm-detail-stats">
                        <div className="acm-stat-pill"><span>{g.subdecks.length}</span> Subdecks</div>
                        <div className="acm-stat-pill"><span>{g.totalCards.toLocaleString('pt-BR')}</span> Cards</div>
                    </div>
                    <div className="acm-detail-section">
                        <h4>Subdecks</h4>
                        {g.subdecks.map(sd => (
                            <div key={sd.subdeckId} className="acm-detail-list-item"
                                onClick={() => setSelected({ type: 'subdeck', data: sd, groupName: g.groupName, deckName: selected.deckName, fieldName: selected.fieldName })}>
                                <FileText size={14} />
                                <span>{sd.subdeckName}</span>
                                <span className="acm-detail-list-count">{sd.cardCount} cards</span>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        if (selected.type === 'subdeck') {
            const sd = selected.data;
            return (
                <div className="acm-detail-content">
                    <div className="acm-detail-header">
                        <FileText size={22} className="acm-icon-subdeck" />
                        <h3>{sd.subdeckName}</h3>
                    </div>
                    <p className="acm-detail-breadcrumb">
                        {selected.fieldName} › {selected.deckName}{selected.groupName ? ` › ${selected.groupName}` : ''}
                    </p>
                    <div className="acm-detail-stats">
                        <div className="acm-stat-pill"><span>{sd.cardCount.toLocaleString('pt-BR')}</span> Cards</div>
                        {sd.apostilaCount > 0 && <div className="acm-stat-pill green"><span>✓</span> Apostila</div>}
                        <div className="acm-stat-pill"><span>{sd.type === 'apostila' ? 'Apostila' : 'Flashcards'}</span></div>
                    </div>
                    {sd.isBlocked && <p className="acm-detail-warning">⚠️ Subdeck bloqueado</p>}
                </div>
            );
        }

        return null;
    };

    // ─── Render ────────────────────────────────────────────────────────────────

    if (isLoading) return (
        <div className="acm-loading">
            <RefreshCw size={28} className="acm-spin" />
            <p>Carregando mapa de conteúdo…</p>
        </div>
    );

    if (error) return (
        <div className="acm-error">
            <p>Erro ao carregar: {(error as Error).message}</p>
            <button onClick={() => queryClient.invalidateQueries({ queryKey: ['admin-content-map'] })}>
                Tentar novamente
            </button>
        </div>
    );

    return (
        <div className="acm-root">
            {/* Header */}
            <div className="adm-page-header acm-header">
                <div>
                    <h1 className="adm-page-title" style={{ margin: 0 }}>Mapa de Conteúdo</h1>
                </div>
                <div className="acm-header-right">
                    <button
                        className="acm-copy-org-btn"
                        onClick={copyOrgTree}
                        title="Copiar organização completa"
                    >
                        {copiedOrg ? <Check size={14} /> : <Copy size={14} />}
                        {copiedOrg ? 'Copiado!' : 'Copiar organização'}
                    </button>
                    <span className="acm-updated">
                        Atualizado {new Date(dataUpdatedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    <button className="acm-refresh-btn" onClick={() => queryClient.invalidateQueries({ queryKey: ['admin-content-map'] })}>
                        <RefreshCw size={16} />
                    </button>
                </div>
            </div>

            {/* Contadores de resumo */}
            <div className="acm-summary">
                <div className="acm-summary-pill">
                    <span className="acm-summary-val">{totals.fields}</span>
                    <span className="acm-summary-label">Áreas</span>
                </div>
                <div className="acm-summary-pill">
                    <span className="acm-summary-val">{totals.decks}</span>
                    <span className="acm-summary-label">Decks</span>
                </div>
                <div className="acm-summary-pill">
                    <span className="acm-summary-val">{totals.subdecks}</span>
                    <span className="acm-summary-label">Subdecks</span>
                </div>
                <div className="acm-summary-pill">
                    <span className="acm-summary-val">{totals.cards.toLocaleString('pt-BR')}</span>
                    <span className="acm-summary-label">Cards</span>
                </div>
            </div>

            {/* Barra de busca */}
            <div className="acm-search-wrap">
                <Search size={16} className="acm-search-icon" />
                <input
                    className="acm-search"
                    placeholder="Buscar campo, deck, grupo ou subdeck…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
                {search && (
                    <button className="acm-search-clear" onClick={() => setSearch('')}>
                        <X size={14} />
                    </button>
                )}
            </div>

            {/* Corpo: árvore + painel lateral */}
            <div className="acm-body">
                <div className="acm-tree">
                    {filteredFields.length === 0 && (
                        <div className="acm-empty">
                            <p>Nenhum resultado para "{search}"</p>
                        </div>
                    )}
                    {filteredFields.map(renderField)}
                </div>

                <div className="acm-detail-panel">
                    <div className="acm-detail-panel-header">
                        <span>Detalhes</span>
                        {selected && (
                            <button className="acm-detail-close" onClick={() => setSelected(null)}>
                                <X size={16} />
                            </button>
                        )}
                    </div>
                    {renderDetailPanel()}
                </div>
            </div>
        </div>
    );
};

export default AdminContentMap;

